'use client'

/**
 * Gedeelde document-/PDF-preview-viewer (STREEP).
 *
 * Toont een document inline in een fullscreen overlay zonder het te downloaden:
 * PDF in een `<iframe>`, afbeeldingen in een `<img>`, beide vanaf een object-URL
 * van de geauthenticeerd opgehaalde blob (one network-rit, gedeeld met opslaan).
 *
 * Werkt zowel desktop (backoffice) als mobiel/PWA (portal): fullscreen met een
 * altijd-zichtbare "Sluiten"-knop en een echte opslaan-actie (op iOS-PWA het
 * deelvenster -> "Bewaar in Bestanden"). Bron-URL's zijn altijd same-origin
 * proxy-routes (sessiecookie reist mee); daarom `credentials: 'include'`.
 *
 * Draaien (rotate) op twee niveaus voor scheef geüploade scans:
 *   - Tijdelijk (altijd): CSS-rotatie van de weergave, wijzigt het bestand niet.
 *   - Permanent (alleen met `onRotatePermanent`, d.w.z. `canEdit` op het
 *     document): de aanroeper slaat de gedraaide oriëntatie op als nieuwe
 *     versie (server-side, geaudit). Na succes sluit de viewer + `onRotated`.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Download,
  ExternalLink,
  Loader2,
  RotateCcw,
  RotateCw,
  Save,
  X,
} from 'lucide-react'
import {
  cleanMimeType,
  documentKindFromMime,
  downloadDocument,
  ensureFileExtension,
  isStandaloneDisplay,
  mimeFromFileName,
  triggerBlobDownload,
  type DocumentKind,
} from '../lib/document-blob'

type Status = 'loading' | 'ready' | 'error'

/**
 * Leest een korte, leesbare foutreden uit een niet-ok proxy-respons. De proxy
 * geeft of een JSON-envelope ({ error: { message } }) of platte tekst terug;
 * HTML-foutpagina's negeren we (niet leesbaar voor de gebruiker). Zo toont de
 * viewer de echte reden i.p.v. enkel de generieke zin.
 */
async function readUpstreamReason(res: Response): Promise<string | null> {
  try {
    const text = (await res.text()).trim()
    if (!text) return null
    try {
      const parsed = JSON.parse(text) as { error?: { message?: unknown } }
      const message = parsed?.error?.message
      if (typeof message === 'string' && message.trim()) {
        return message.trim().slice(0, 200)
      }
      // Wel JSON, maar niet de {error:{message}}-vorm: geen leesbare reden,
      // toon niets (de generieke zin volstaat) i.p.v. een ruwe JSON-string.
      return null
    } catch {
      // geen JSON-envelope; val terug op platte tekst (geen HTML-foutpagina)
    }
    if (!text.startsWith('<')) return text.slice(0, 200)
    return null
  } catch {
    return null
  }
}

export interface PdfPreviewModalProps {
  /** Same-origin proxy-URL die het bestand levert (sessiecookie reist mee). */
  url: string
  /**
   * Optionele tweede preview-bron (same-origin proxy-URL). Lukt de hoofd-`url`
   * niet, dan probeert de viewer deze bron (bijvoorbeeld de originele bron-PDF
   * van een geimporteerde factuur) voordat hij de foutstaat toont; in de
   * foutstaat wijzen Download en "Open in nieuw tabblad" ook hierheen.
   */
  fallbackUrl?: string
  /** Weergavenaam in de header én bestandsnaam bij opslaan. */
  fileName: string
  /** Optionele mime-hint; anders afgeleid uit Content-Type of de bestandsnaam. */
  mimeType?: string
  /** Sluit de viewer. */
  onClose: () => void
  /**
   * Permanente rotatie opslaan (alleen documenten met canEdit). Ontvangt de
   * netto rotatie in graden met de klok mee (90/180/270) en slaat die als
   * nieuwe versie op. Afwezig -> alleen tijdelijke (CSS) rotatie.
   */
  onRotatePermanent?: (rotationDegrees: number) => Promise<{ ok: boolean; message?: string }>
  /** Aangeroepen na een geslaagde permanente rotatie (bv. router.refresh). */
  onRotated?: () => void
}

const ROUND = 'inline-flex h-11 min-w-[44px] items-center justify-center gap-1 rounded-full bg-white/10 px-3 text-sm font-medium text-white transition-colors hover:bg-white/20 active:bg-white/25 disabled:opacity-40'
const ERROR_ACTION = 'inline-flex h-11 items-center gap-2 rounded-full bg-white/10 px-4 text-sm font-medium text-white transition-colors hover:bg-white/20 active:bg-white/25'

export function PdfPreviewModal({
  url,
  fallbackUrl,
  fileName,
  mimeType,
  onClose,
  onRotatePermanent,
  onRotated,
}: PdfPreviewModalProps) {
  const [status, setStatus] = useState<Status>('loading')
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [kind, setKind] = useState<DocumentKind>('other')
  const [rotation, setRotation] = useState(0)
  const [savingRotation, setSavingRotation] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [errorReason, setErrorReason] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const blobRef = useRef<Blob | null>(null)
  const typeRef = useRef<string>('application/octet-stream')

  // createPortal mag pas na mount (client) - voorkomt SSR/hydration-mismatch.
  useEffect(() => setMounted(true), [])

  // Haalt het bestand als blob op. AbortController + cancelled-vlag voorkomen
  // setState-na-unmount en een blob-URL-lek als de viewer tijdens het laden sluit.
  // Lukt de hoofd-`url` niet en is er een `fallbackUrl`, dan proberen we die als
  // tweede preview-bron voordat we de foutstaat tonen (bv. de bron-PDF van een
  // geimporteerde factuur).
  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    const candidates = fallbackUrl && fallbackUrl !== url ? [url, fallbackUrl] : [url]
    async function run() {
      setStatus('loading')
      setRotation(0)
      setErrorReason(null)
      let lastReason: string | null = null
      for (const candidate of candidates) {
        try {
          const res = await fetch(candidate, { credentials: 'include', signal: controller.signal })
          if (!res.ok) {
            lastReason = await readUpstreamReason(res)
            continue
          }
          const blob = await res.blob()
          if (cancelled) return
          const type =
            cleanMimeType(res.headers.get('content-type')) ||
            cleanMimeType(mimeType) ||
            mimeFromFileName(fileName) ||
            'application/octet-stream'
          const createdUrl = URL.createObjectURL(blob)
          blobRef.current = blob
          typeRef.current = type
          setObjectUrl(createdUrl)
          setKind(documentKindFromMime(type))
          setStatus('ready')
          return
        } catch {
          if (cancelled || controller.signal.aborted) return
          lastReason = null
        }
      }
      if (!cancelled) {
        setErrorReason(lastReason)
        setStatus('error')
      }
    }
    void run()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [url, fallbackUrl, fileName, mimeType, reloadKey])

  // Blob-URL vrijgeven zodra hij vervangen wordt of de viewer sluit.
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [objectUrl])

  // Esc sluit; achtergrond niet meescrollen terwijl de overlay open is.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const rotateBy = useCallback((delta: number) => {
    setRotation((r) => (r + delta + 360) % 360)
  }, [])

  const handleSaveFile = useCallback(async () => {
    const blob = blobRef.current
    if (!blob || !objectUrl) return
    setActionError(null)
    const downloadName = ensureFileExtension(fileName, typeRef.current)
    const file = new File([blob], downloadName, { type: typeRef.current })
    // Web Share met bestanden = op iOS-PWA het enige betrouwbare opslaan
    // (deelvenster -> "Bewaar in Bestanden" / "Foto's"). Feature-detect met
    // canShare; valt anders terug op een directe download-anchor (desktop/Android).
    const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean }
    if (typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: fileName })
        return
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (isStandaloneDisplay()) {
          setActionError('Opslaan mislukt - probeer opnieuw.')
          return
        }
      }
    }
    triggerBlobDownload(blob, downloadName)
  }, [fileName, objectUrl])

  // In de foutstaat is er (nog) geen blob; haal het bestand vers op via de
  // proxy en download het direct. Bron = de fallback indien meegegeven, anders
  // de hoofd-url.
  const handleErrorDownload = useCallback(async () => {
    setActionError(null)
    try {
      await downloadDocument(fallbackUrl ?? url, fileName, { mimeType })
    } catch {
      setActionError('Downloaden mislukt - probeer opnieuw.')
    }
  }, [fallbackUrl, url, fileName, mimeType])

  const handleSaveRotation = useCallback(async () => {
    if (!onRotatePermanent || rotation === 0) return
    setSavingRotation(true)
    setActionError(null)
    try {
      const r = await onRotatePermanent(rotation)
      if (!r.ok) {
        setActionError(r.message ?? 'Rotatie opslaan mislukt - probeer opnieuw.')
        return
      }
      onRotated?.()
      onClose()
    } catch {
      setActionError('Rotatie opslaan mislukt - probeer opnieuw.')
    } finally {
      setSavingRotation(false)
    }
  }, [onRotatePermanent, rotation, onRotated, onClose])

  if (!mounted) return null

  const canRotate = status === 'ready' && (kind === 'image' || kind === 'pdf')
  const quarterTurned = rotation === 90 || rotation === 270
  // Bij een kwartslag de afbeelding inkrimpen zodat de gedraaide lange zijde
  // binnen de korte zijde van de container blijft (geen clipping). Grof maar
  // robuust zonder de container te meten.
  const mediaStyle = {
    transform: `rotate(${rotation}deg)`,
    transition: 'transform 0.2s ease',
    ...(quarterTurned ? { maxWidth: '75vh', maxHeight: '75vw' } : {}),
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={fileName}
      className="fixed inset-0 z-[60] flex flex-col bg-black/95"
      onClick={onClose}
    >
      <header
        className="flex items-center gap-2 px-3 pb-2 pt-[calc(env(safe-area-inset-top)+0.5rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={onClose} aria-label="Sluiten" className={ROUND}>
          <X className="h-5 w-5" aria-hidden="true" />
          <span className="hidden sm:inline">Sluiten</span>
        </button>
        <p className="min-w-0 flex-1 truncate text-center text-sm text-white/80">{fileName}</p>
        <div className="flex items-center gap-1">
          {canRotate && (
            <>
              <button
                type="button"
                onClick={() => rotateBy(-90)}
                aria-label="Linksom draaien"
                className={ROUND}
              >
                <RotateCcw className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => rotateBy(90)}
                aria-label="Rechtsom draaien"
                className={ROUND}
              >
                <RotateCw className="h-5 w-5" aria-hidden="true" />
              </button>
              {onRotatePermanent && rotation !== 0 && (
                <button
                  type="button"
                  onClick={handleSaveRotation}
                  disabled={savingRotation}
                  aria-label="Rotatie opslaan"
                  className={ROUND}
                >
                  {savingRotation ? (
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Save className="h-5 w-5" aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">Opslaan</span>
                </button>
              )}
            </>
          )}
          {status === 'ready' && (
            <button type="button" onClick={handleSaveFile} aria-label="Downloaden" className={ROUND}>
              <Download className="h-5 w-5" aria-hidden="true" />
              <span className="hidden sm:inline">Download</span>
            </button>
          )}
        </div>
      </header>

      {actionError ? (
        <p
          role="alert"
          className="mx-3 mb-1 rounded-md bg-red-500/90 px-3 py-2 text-center text-xs font-medium text-white"
          onClick={(e) => e.stopPropagation()}
        >
          {actionError}
        </p>
      ) : null}

      <div
        className="flex flex-1 items-center justify-center overflow-hidden p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        {status === 'loading' ? (
          <div className="flex flex-col items-center gap-3 text-white/80">
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
            <p className="text-sm">Laden…</p>
          </div>
        ) : status === 'error' ? (
          <div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
            <p className="text-sm text-white/80">Kon het document niet openen.</p>
            {errorReason ? <p className="text-xs text-white/50">{errorReason}</p> : null}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={ERROR_ACTION}>
                Opnieuw proberen
              </button>
              <button type="button" onClick={handleErrorDownload} className={ERROR_ACTION}>
                <Download className="h-4 w-4" aria-hidden="true" />
                Download
              </button>
              <a
                href={fallbackUrl ?? url}
                target="_blank"
                rel="noopener noreferrer"
                className={ERROR_ACTION}
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Open in nieuw tabblad
              </a>
            </div>
          </div>
        ) : kind === 'image' && objectUrl ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img src={objectUrl} alt={fileName} className="max-h-full max-w-full object-contain" style={mediaStyle} />
        ) : kind === 'pdf' && objectUrl ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2">
            <iframe
              src={objectUrl}
              title={fileName}
              className="h-full w-full rounded-md bg-white"
              style={rotation !== 0 ? { transform: `rotate(${rotation}deg)`, transition: 'transform 0.2s ease' } : undefined}
            />
            {isStandaloneDisplay() ? (
              <p className="shrink-0 text-center text-xs text-white/50">
                Komt de PDF niet in beeld? Tik op &ldquo;Download&rdquo; om hem te openen.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 text-center">
            <p className="text-sm text-white/80">Dit bestandstype kan niet in de app worden getoond.</p>
            <p className="text-xs text-white/50">Gebruik &ldquo;Download&rdquo; om het bestand te bewaren of te openen.</p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
